import { useEffect, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import PrintIcon from '@mui/icons-material/Print';
import { Box, IconButton, Tooltip } from '@mui/material';

import supportingDocumentService from '../../service/supportingDocument.service';
import { ButtonUI, DataGridIdentifiers, DatagridPremiumUi, DialogUI } from '../Interface';
import { dataGridPremiumColumns } from '../../utils/helper';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';

const getSupportingDocumentRowId = (row) =>
  `${row.id ?? 'row'}-${row.lineNum}-${row.itemCode}-${row.batchSnId ?? 'batch'}-${
    row.sourceBatchSnId ?? 'sourceBatch'
  }-${row.supportingDocumentTypeId}-${row.batchSnAttachmentId ?? 'batchAttachment'}-${
    row.productionAttachmentId ?? 'productionAttachment'
  }`;

const getExternalFileName = (row) =>
  row.fileName ?? row.batchSnAttachment?.name ?? row.productionAttachment?.name ?? '';

const getSourceBatchSn = (row) =>
  typeof row.sourceBatchSn === 'object' ? row.sourceBatchSn?.batchSn ?? '' : row.sourceBatchSn ?? '';

const getExternalSourceLabel = (row) => {
  if (row.productionId != null || row.productionAttachmentId != null) return 'Gyártási csatolmány';
  if (row.sourceBatchSnId != null) return 'Alapanyag sarzs';
  if (!row.generated) return 'Késztermék sarzs';
  return '';
};

const SupportingDocumentDialog = ({ open, onClose, docId, documentTypeId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [printingRowId, setPrintingRowId] = useState(null);

  useEffect(() => {
    if (!open || !docId || !documentTypeId) return;

    let active = true;
    setRows([]);
    setLoading(true);

    supportingDocumentService
      .getData(docId, documentTypeId)
      .then((data) => {
        if (active) setRows(Array.isArray(data) ? data : []);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [docId, documentTypeId, open]);

  const handlePrint = async (row) => {
    const rowId = getSupportingDocumentRowId(row);
    setPrintingRowId(rowId);

    try {
      addNotification({ content: 'Nyomtatás elindult!', type: NotificationType.INFO });
      await supportingDocumentService.print({
        documentTypeId: row.documentTypeId,
        docId: row.docId,
        lineNum: row.lineNum,
        itemCode: row.itemCode,
        batchSnId: row.batchSnId ?? null,
        supportingDocumentTypeId: row.supportingDocumentTypeId,
        batchSnAttachmentId: row.batchSnAttachmentId ?? null,
        productionAttachmentId: row.productionAttachmentId ?? null,
      });

      const data = await supportingDocumentService.getData(docId, documentTypeId);
      setRows(Array.isArray(data) ? data : []);
    } finally {
      setPrintingRowId(null);
    }
  };

  const handleClose = () => {
    if (printingRowId !== null) return;
    onClose();
  };

  const columns = dataGridPremiumColumns([
    {
      field: 'itemCode',
      headerName: 'Cikkszám',
      flex: 0.8,
      minWidth: 140,
    },
    {
      field: 'batchSn',
      headerName: 'Sarzs',
      flex: 0.8,
      minWidth: 140,
    },
    {
      field: 'sourceItemCode',
      headerName: 'Alapanyag cikkszáma',
      flex: 0.8,
      minWidth: 160,
    },
    {
      field: 'sourceBatchSn',
      headerName: 'Alapanyag sarzsa',
      flex: 0.8,
      minWidth: 160,
      valueGetter: (_value, row) => getSourceBatchSn(row),
    },
    {
      field: 'name',
      headerName: 'Kiegészítő dokumentum neve',
      flex: 1.5,
      minWidth: 240,
      renderCell: (params) => (
        <Tooltip title={params.row.message ?? ''}>
          <span>{params.row.name}</span>
        </Tooltip>
      ),
    },
    {
      field: 'generated',
      headerName: 'Típus',
      minWidth: 120,
      valueGetter: (_value, row) => (row.generated ? 'Generált' : 'Külső'),
    },
    {
      field: 'fileName',
      headerName: 'Fájlnév',
      flex: 1,
      minWidth: 200,
      valueGetter: (_value, row) => (row.generated ? '' : getExternalFileName(row)),
    },
    {
      field: 'externalSource',
      headerName: 'Forrás',
      flex: 0.8,
      minWidth: 170,
      valueGetter: (_value, row) => getExternalSourceLabel(row),
    },
    {
      field: 'message',
      headerName: 'Üzenet',
      flex: 1,
      minWidth: 220,
    },
    {
      field: 'print',
      headerName: 'Nyomtatás',
      sortable: false,
      filterable: false,
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const rowId = getSupportingDocumentRowId(params.row);
        const externalAttachmentIdMissing =
          params.row.batchSnAttachmentId == null && params.row.productionAttachmentId == null;
        const externalFileMissing =
          !params.row.generated && (externalAttachmentIdMissing || !getExternalFileName(params.row));
        const disabled = Boolean(params.row.message) || externalFileMissing || printingRowId !== null;
        const tooltip =
          params.row.message ??
          (externalFileMissing ? 'A külső dokumentumhoz nincs feltöltött csatolmány.' : 'Nyomtatás');

        return (
          <Tooltip title={tooltip}>
            <span>
              <IconButton
                size="small"
                disabled={disabled}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  handlePrint(params.row);
                }}
              >
                <PrintIcon fontSize="small" color={printingRowId === rowId ? 'disabled' : 'inherit'} />
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: 'printed',
      headerName: 'Nyomtatva',
      type: 'boolean',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (params.row.printed ? <CheckIcon color="success" fontSize="small" /> : null),
    },
  ]);

  return (
    <DialogUI open={open} onClose={handleClose} headerContent="Kísérő dokumentumok" maxWidth="xl" fullWidth>
      <Box mt="1rem">
        <DatagridPremiumUi
          datagridIdentifier={DataGridIdentifiers.supportingDocument}
          datagridInitialState={{ density: 'compact' }}
          rows={rows}
          columns={columns}
          loading={loading}
          hideFooter
          height={550}
          paginationAndfilterMode="client"
          disableSave
          headerFilters={false}
          getRowId={getSupportingDocumentRowId}
        />
      </Box>

      <Box display="flex" justifyContent="center" mt="1rem">
        <ButtonUI text="Bezárás" className="bg-primary" onClick={handleClose} disabled={printingRowId !== null} />
      </Box>
    </DialogUI>
  );
};

export default SupportingDocumentDialog;
